import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../../core/projects.service';
import * as fromRoot from '../../../../../../../../../state/app.state';
import {
  CreateDeliveryTrackingFieldRequest,
  UpdateDeliveryTrackingFieldRequest,
} from '../../../../../../../core/projects.actions';

@Component({
    selector: 'app-create-or-edit-delivery-tracking',
    templateUrl: './create-or-edit-delivery-tracking.component.html',
    styleUrls: ['./create-or-edit-delivery-tracking.component.scss'],
    standalone: false
})
export class CreateOrEditDeliveryTrackingComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  form = new FormGroup({
    label: new FormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
    name: new FormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
    type: new FormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
    uom: new FormControl({ value: '', disabled: false }),
    isOptional: new FormControl({ value: false, disabled: false }),
    required: new FormControl({ value: false, disabled: false }),
    required_min: new FormControl({ value: null as number | null, disabled: false }),
    required_max: new FormControl({ value: null as number | null, disabled: false }),
    range_min: new FormControl({ value: null as number | null, disabled: false }),
    range_max: new FormControl({ value: null as number | null, disabled: false }),
  });

  typeOptions: { value: string; label: string }[] = [
    { value: 'number', label: 'Number' },
    { value: 'string', label: 'String' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'json', label: 'JSON' },
  ];
  isFormDirty = false;
  projectId: string | null = null;
  errorMessages: any = {};
  errorMessageMap: Record<string, Record<string, string>> = {
    label: { required: 'This field is required.' },
    name: { required: 'This field is required.' },
    type: { required: 'This field is required.' },
  };

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private snackBar: MatSnackBar
  ) {}

  private labelToName(label: string): string {
    return (label || '').trim().toLowerCase().replace(/\s+/g, '_');
  }

  onIsOptionalToggle(event: MatSlideToggleChange): void {
    this.form.patchValue({ isOptional: event.checked });
  }

  onRequiredToggle(event: MatSlideToggleChange): void {
    this.form.patchValue({ required: event.checked });
  }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    } else {
      const url = this.router.url;
      const path = url.split('?')[0];
      const segments = path.split('/');
      this.projectId = segments[2] ?? null;
    }

    if (!this.projectId && this.data?.projectId) {
      this.projectId = this.data.projectId;
    }

    if (this.data?.id) {
      const vr = this.data.validation_rules ?? {};
      const rv = this.data.range_values ?? {};
      this.form.patchValue({
        name: this.data.name ?? null,
        label: this.data.label ?? null,
        type: this.data.type ?? null,
        uom: this.data.uom ?? null,
        isOptional: this.data.is_optional ?? false,
        required: vr.required ?? false,
        required_min: vr.required_min ?? vr.min ?? null,
        required_max: vr.required_max ?? vr.max ?? null,
        range_min: rv.min ?? null,
        range_max: rv.max ?? null,
      });
    }

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });

    this.form.controls['label'].valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe(labelValue => {
        const nameValue = this.labelToName(labelValue ?? '');
        this.form.patchValue({ name: nameValue }, { emitEvent: false });
      });
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(
      this.form,
      this.errorMessageMap,
      currentField
    );
    if (validationErrors) {
      this.errorMessages = { ...this.errorMessages, ...validationErrors };
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    if (!this.projectId) {
      this.snackBar.open('Project context is missing', '', { duration: 3000 });
      return;
    }

    const raw = this.form.getRawValue();
    const payload = {
      id: this.data?.id ?? null,
      display_order: this.data?.display_order ?? 0,
      name: raw.name,
      label: raw.label,
      type: raw.type,
      uom: raw.uom,
      estimate_station_property: null,
      validation_rules: {
        required: raw.required ?? false,
        min: raw.required_min ?? null,
        max: raw.required_max ?? null,
      },
      recent_field_id: null,
      recent_field_label: null,
      is_optional: raw.isOptional ?? false,
      range_values: {
        min: raw.range_min ?? null,
        max: raw.range_max ?? null,
      },
      projectId: this.projectId,
    };

    if (payload.id) {
      this.store.dispatch(UpdateDeliveryTrackingFieldRequest({ payload }));
    } else {
      this.store.dispatch(CreateDeliveryTrackingFieldRequest({ payload }));
    }
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
